import React, { useEffect, useState } from "react";
import styles from "./CalendarDropDown.module.scss";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default function CalendarDropDown({ onCancel, onApply, dataPeriod }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [startMonth, setStartMonth] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [endMonth, setEndMonth] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [hoverMonth, setHoverMonth] = useState<{
    year: number;
    month: number;
  } | null>(null);

  const [selectedMonth, setSelectedMonth] = useState(
    year == currentYear ? { month: currentMonth, year: currentYear } : null
  );

  function getMonthYearFromDataPeriodArray(s: string) {
    const [dd, mm, yyyy] = s.split("-");
    const month = Number(mm) - 1;
    const year = Number(yyyy);
    if (Number.isNaN(month) || Number.isNaN(year) || month < 0 || month > 11) {
      throw new Error(`Invalid date string: ${s}`);
    }
    return { month, year };
  }

  useEffect(() => {
    if (dataPeriod && dataPeriod.length > 0) {
      const startMonth = getMonthYearFromDataPeriodArray(dataPeriod[0]);
      const endMonth = getMonthYearFromDataPeriodArray(dataPeriod[1]);
      setStartMonth(startMonth);
      if (endMonth?.month && endMonth?.year) {
        setEndMonth(endMonth);
      }
    }
  }, [dataPeriod]);

  const handleMonthClick = (month: number) => {
    if (!startMonth) {
      // no start yet, set start
      setStartMonth({ year, month });
      setEndMonth(null);
    } else if (!endMonth) {
      // start already chosen, deciding end
      if (
        year < startMonth.year ||
        (year === startMonth.year && month < startMonth.month)
      ) {
        // clicked on earlier month, reset start
        setStartMonth({ year, month });
        setEndMonth(null);
        setSelectedMonth({ year, month });
      } else {
        // clicked on valid future month, lock end
        setEndMonth({ year, month });
        setSelectedMonth({ year, month });
      }
    } else {
      // range already set, reset new start
      console.log({ startMonth, endMonth });
      setStartMonth({ year, month });
      setSelectedMonth({ year, month });
      setEndMonth(null);
    }
  };

  const handleMonthHover = (year: number, month: number) => {
    if (!startMonth && !endMonth) {
      setHoverMonth({ year, month });
      return;
    }
    if (!startMonth || endMonth) return;
    if (
      year > startMonth.year ||
      (year === startMonth.year && month >= startMonth.month)
    ) {
      setHoverMonth({ year, month });
    } else {
      setHoverMonth(null);
    }
  };

  const isInRange = (year: number, month: number) => {
    if (!startMonth) return false;

    const end = endMonth || hoverMonth;
    if (!end) return false;

    const startVal = startMonth.year * 12 + startMonth.month;
    const endVal = end.year * 12 + end.month;
    const thisVal = year * 12 + month;

    return thisVal > startVal && thisVal <= endVal;
  };

  return (
    <div className={styles.container}>
      <div className={styles.upperContainer}>
        {/* Header */}
        <div className={styles.header}>
          <button onClick={() => setYear(year - 1)} className={styles.arrowBtn}>
            <HiChevronLeft />
          </button>
          <div className={styles.year}>{year}</div>
          <button
            onClick={() => setYear(year + 1)}
            className={`${styles.arrowBtn} ${
              year >= currentYear && styles.disabledArrowBtn
            }`}
          >
            <HiChevronRight />
          </button>
        </div>

        {/* Month rows */}
        <div className={styles.monthGrid}>
          {Array.from({ length: 4 }).map((_, rowIndex) => (
            <div key={rowIndex} className={styles.monthRow}>
              {months.slice(rowIndex * 3, rowIndex * 3 + 3).map((m, i) => {
                const monthIndex = rowIndex * 3 + i;
                const isFuture =
                  year > currentYear ||
                  (year === currentYear && monthIndex > currentMonth);
                const monthDiff =
                  (currentYear - year) * 12 + (currentMonth - monthIndex);
                const isOlderThan18Months = monthDiff > 18;
                // console.log("data here", monthIndex === endMonth?.month);
                return (
                  <div
                    className={`${styles.btnContainer} 
                    ${
                      !isFuture &&
                      monthIndex === selectedMonth?.month &&
                      year === selectedMonth?.year &&
                      (startMonth?.month === null || !(startMonth?.month >= 0))
                        ? styles.active
                        : ""
                    }
                    ${
                      !isFuture &&
                      startMonth?.year === year &&
                      startMonth?.month === monthIndex
                        ? styles.active
                        : ""
                    }
                    ${
                      !isFuture &&
                      endMonth?.year === year &&
                      endMonth?.month === monthIndex
                        ? styles.active
                        : ""
                    }
                    ${
                      !isFuture &&
                      (monthIndex !== endMonth?.month ||
                        year !== endMonth?.year) &&
                      isInRange(year, monthIndex)
                        ? styles.range
                        : ""
                    }`}
                    // onClick={() => setSelectedMonth(monthIndex)}
                    onClick={() => handleMonthClick(monthIndex)}
                    onMouseEnter={() => handleMonthHover(year, monthIndex)}
                  >
                    <button
                      key={m}
                      className={`${styles.monthBtn} ${
                        monthIndex === selectedMonth?.month ? styles.active : ""
                      }`}
                      disabled={isFuture || isOlderThan18Months}
                    >
                      {m}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.btnContainer}>
          <MeritoButton
            onClick={onCancel}
            type="tertiary"
            size="md"
            //    className={styles.cancelBtn}>
          >
            Cancel
          </MeritoButton>
          <MeritoButton
            onClick={() =>
              onApply({
                startMonth,
                endMonth,
              })
            }
            // className={styles.applyBtn}
            type="primary-1"
            size="md"
          >
            Apply
          </MeritoButton>
        </div>
      </div>
    </div>
  );
}
